import { vetor, type Vetor3 } from '../geometria/vetor3'
import type { TipoColisao } from './timelineCinematica'

export type TrajetoriaFrontal = 'upAndOver' | 'downAndUnder'
export type TipoSobreposicaoFrontal = 'total' | 'smallOverlap'
export type LadoImpactoLateral = 'nearSide' | 'farSide'

export interface DefinicaoTrajetoriaFrontal {
  id: TrajetoriaFrontal
  nome: string
  subtitulo: string
  descricao: string
  mecanismo: string
  pontosImpacto: readonly string[]
  lesoesPrincipais: readonly string[]
  deslocamentoBasePelve: Vetor3
  deslocamentoBaseCabeca: Vetor3
}

export const TRAJETORIAS_FRONTAIS: Record<TrajetoriaFrontal, DefinicaoTrajetoriaFrontal> = {
  upAndOver: {
    id: 'upAndOver',
    nome: 'Por Cima do Volante (Up-and-Over)',
    subtitulo: 'Projeção crânio-torácica superior',
    descricao: 'O corpo é arremessado para cima e para a frente sobre o aro do volante em direção ao para-brisa e coluna A.',
    mecanismo: 'A pelve se eleva do assento; a inércia projeta a cabeça contra o vidro laminado e o tórax/abdome superior contra o topo do volante.',
    pontosImpacto: ['Cabeça no para-brisa', 'Face no retrovisor/coluna A', 'Esterno no aro do volante'],
    lesoesPrincipais: [
      'TCE grave por desaceleração e cisalhamento axonal difuso',
      'Trauma facial com fraturas de Le Fort e perda de via aérea',
      'Lesão na coluna cervical alta por compressão axial / hiperextensão',
      'Rotura traumática de aorta e contusão miocárdica/pulmonar',
    ],
    deslocamentoBasePelve: vetor(0, 0.12, 0.28),
    deslocamentoBaseCabeca: vetor(0, 0.32, 0.48),
  },
  downAndUnder: {
    id: 'downAndUnder',
    nome: 'Por Baixo do Painel (Down-and-Under)',
    subtitulo: 'Mergulho pélvico e impacto patelar',
    descricao: 'O ocupante escorrega para a frente e para baixo no assento, encaixando os joelhos no painel inferior.',
    mecanismo: 'Os pés travam na pedaleira e os joelhos colidem com o painel inferior; o tronco dobra secundariamente sobre a coluna de direção.',
    pontosImpacto: ['Joelhos/patelas no painel inferior', 'Pés na pedaleira', 'Tórax inferior na coluna de direção'],
    lesoesPrincipais: [
      'Fratura cominutiva de patela e luxação articular',
      'Fratura diafisária de fêmur com grande perda sanguínea oculta',
      'Luxação posterior de quadril com fratura do rebordo acetabular e lesão do nervo ciático',
      'Fratura-luxação de calcâneo e tornozelo por intrusão dos pedais',
    ],
    deslocamentoBasePelve: vetor(0, -0.06, 0.38),
    deslocamentoBaseCabeca: vetor(0, -0.05, 0.32),
  },
}

export interface DefinicaoSmallOverlap {
  nome: string
  descricao: string
  mecanismo: string
  anguloRotacaoYawRad: number
  intrusaoCabineCm: number
  lesoesCriticas: readonly string[]
  implicacaoSalvamento: string
}

export const DADOS_SMALL_OVERLAP: DefinicaoSmallOverlap = {
  nome: 'Pequena Sobreposição Dianteira (Small Overlap 25%)',
  descricao: 'Impacto concentrado em apenas 25% da dianteira esquerda contra poste, árvore ou veículo contrário.',
  mecanismo: 'A longarina longitudinal de absorção é completamente contornada. A roda dianteira esquerda é empurrada para trás como um aríete, arrombando a coluna A e o piso.',
  anguloRotacaoYawRad: 0.42, // ~24 graus de rotação brusca
  intrusaoCabineCm: 38,
  lesoesCriticas: [
    'Encarceramento físico tipo II severo em membros inferiores',
    'Fratura exposta bimaléolar / Lisfranc por invasão direta da caixa de roda',
    'Torção cervical brusca por momento angular de guinada (Yaw) assimétrico',
    'Trauma torácico por invasão lateral da coluna A e painel',
  ],
  implicacaoSalvamento: 'Porta dianteira soldada mecanicamente pela deformação em paralelogramo da soleira; necessidade prioritária de expansor hidráulico para alívio de painel.',
}

export interface DefinicaoImpactoLateral {
  lado: LadoImpactoLateral
  nome: string
  subtitulo: string
  descricao: string
  intrusaoDireta: boolean
  lesoesTipicas: readonly string[]
}

export const DADOS_IMPACTO_LATERAL: Record<LadoImpactoLateral, DefinicaoImpactoLateral> = {
  nearSide: {
    lado: 'nearSide',
    nome: 'Lado Próximo ao Impacto (Near-Side)',
    subtitulo: 'Colapso imediato do espaço de sobrevivência',
    descricao: 'A estrutura lateral (porta e coluna B) invade o corpo do ocupante antes de qualquer movimento relativo livre.',
    intrusaoDireta: true,
    lesoesTipicas: [
      'Fraturas múltiplas de arcos costais homolaterais com tórax instável',
      'Contusão pulmonar grave e hemopneumotórax maciço',
      'Laceração de baço (impacto do lado motorista) ou fígado (lado passageiro)',
      'Fratura do anel pélvico por compressão lateral com sangramento retroperitoneal',
    ],
  },
  farSide: {
    lado: 'farSide',
    nome: 'Lado Oposto ao Impacto (Far-Side)',
    subtitulo: 'Projeção interna e choque medial',
    descricao: 'Ocupante é acelerado em direção ao interior do veículo, tendendo a escapar da faixa diagonal do cinto de 3 pontos.',
    intrusaoDireta: false,
    lesoesTipicas: [
      'Choque violento contra o console central, alavanca de freio ou ocupante adjacente',
      'Torção cervical e torácica por saída do cinto diagonal',
      'Traumatismo craniano por impacto na estrutura medial ou cabeça do passageiro ao lado',
    ],
  },
}

/**
 * Retorna os ajustes cinemáticos e angulares proporcionados pelas variações de colisão.
 */
export function obterAjusteCinematicaVariacao(
  tipoColisao: TipoColisao,
  tempo: number,
  opcoes?: {
    trajetoriaFrontal?: TrajetoriaFrontal
    smallOverlap?: boolean
    ladoLateral?: LadoImpactoLateral
  },
) {
  let deltaYaw = 0
  let deltaPosicaoPelve = vetor(0, 0, 0)
  let deltaPosicaoCabeca = vetor(0, 0, 0)

  if (tipoColisao === 'frontal') {
    if (opcoes?.smallOverlap) {
      // Rotação assimétrica do veículo no small overlap
      const fatorTempo = Math.min(1, Math.max(0, (tempo - 0.15) / 0.6))
      deltaYaw = DADOS_SMALL_OVERLAP.anguloRotacaoYawRad * fatorTempo
      deltaPosicaoPelve = vetor(-0.06 * fatorTempo, 0, 0.08 * fatorTempo)
    }

    if (opcoes?.trajetoriaFrontal === 'downAndUnder') {
      const def = TRAJETORIAS_FRONTAIS.downAndUnder
      const fatorTempo = Math.min(1, Math.max(0, (tempo - 0.2) / 0.5))
      deltaPosicaoPelve = vetor(0, def.deslocamentoBasePelve.y * fatorTempo, def.deslocamentoBasePelve.z * fatorTempo)
      deltaPosicaoCabeca = vetor(0, def.deslocamentoBaseCabeca.y * fatorTempo, def.deslocamentoBaseCabeca.z * fatorTempo)
    } else if (opcoes?.trajetoriaFrontal === 'upAndOver') {
      const def = TRAJETORIAS_FRONTAIS.upAndOver
      const fatorTempo = Math.min(1, Math.max(0, (tempo - 0.2) / 0.5))
      deltaPosicaoPelve = vetor(0, def.deslocamentoBasePelve.y * fatorTempo, def.deslocamentoBasePelve.z * fatorTempo)
      deltaPosicaoCabeca = vetor(0, def.deslocamentoBaseCabeca.y * fatorTempo, def.deslocamentoBaseCabeca.z * fatorTempo)
    }
  }

  return {
    deltaYaw,
    deltaPosicaoPelve,
    deltaPosicaoCabeca,
  }
}
